import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRightIcon, EyeIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { CHANNEL_ICONS, CHANNEL_LABELS } from '../../data/channels';
import type { ChannelsState } from '../../types/composer';
import type { ChannelKey } from '../../types/notice';
import type { PreviewTab } from './NoticePreview';
interface PreviewMiniPanelProps {
  channels: ChannelsState;
  title: string;
  categoryLabel: string;
  contentLang: 'fr' | 'en';
  /** Reopens the preview, optionally on a given channel. */
  onOpen: (tab?: PreviewTab) => void;
}
const ORDER: ChannelKey[] = ['app', 'push', 'sms', 'email', 'voice', 'website', 'social', 'screens'];

/** Compact card shown at the top right when the preview panel is closed. */
export function PreviewMiniPanel({
  channels,
  title,
  categoryLabel,
  contentLang,
  onOpen
}: PreviewMiniPanelProps) {
  const {
    t,
    lang
  } = useLanguage();
  const active = ORDER.filter(key => channels[key].active);
  return <motion.section initial={{
    opacity: 0,
    y: -6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.18,
    ease: 'easeOut'
  }} aria-label={t('Aperçu réduit', 'Collapsed preview')} className="w-[300px] bg-white border border-slate-200 rounded-xl shadow-[0_4px_16px_-8px_rgba(15,23,42,0.18)] overflow-hidden" data-id="element-4380" data-mp="12puljzqth3oo">
      <button type="button" onClick={() => onOpen()} className="w-full flex items-center gap-1 px-4 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4F46E5]" data-id="element-4381" data-mp="1hlsj9mge2g30">
        {t('Aperçu', 'Preview')}
        <ChevronRightIcon className="w-4 h-4 text-slate-400" aria-hidden="true" data-id="element-4382" data-mp="ypj2usi0ldfu" />
      </button>

      <div className="border-t border-slate-100 px-4 py-3" data-id="element-4383" data-mp="1aomqfc5u7bl">
        <p className="text-sm font-semibold text-slate-900" data-id="element-4384" data-mp="182dmb9mfjr9">
          {t('Canaux', 'Channels')} <span className="font-normal text-slate-400" data-id="element-4385" data-mp="66vi5e6xk0fa">{active.length}</span>
        </p>
        {active.length === 0 ? <p className="mt-1.5 text-xs text-slate-400" data-id="element-4386" data-mp="3pev0brcwahqq">
            {t('Aucun canal sélectionné pour le moment.', 'No channel selected yet.')}
          </p> : <ul className="mt-1.5 -mx-2" data-id="element-4387" data-mp="806cfx3s9q4s">
            {active.map(key => {
          const Icon = CHANNEL_ICONS[key];
          return <li key={key} data-id="element-4388" data-mp="3be0qr5e42aqh">
                  <button type="button" onClick={() => onOpen(key as PreviewTab)} className="group w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-left hover:bg-slate-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]" data-id="element-4389" data-mp="3bfc33tlmgqt7">
                    <span className="w-6 h-6 rounded-md bg-slate-100 flex items-center justify-center shrink-0" data-id="element-4390" data-mp="10miu1ejfnd1d">
                      <Icon className="w-3.5 h-3.5 text-slate-500" aria-hidden="true" data-id="element-4391" data-mp="voh6ohez4rk3" />
                    </span>
                    <span className="flex-1 min-w-0 text-sm text-slate-700 truncate" data-id="element-4392" data-mp="1365qkzg0vxi5">{CHANNEL_LABELS[key][lang]}</span>
                    <EyeIcon className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#4F46E5] transition-colors" aria-label={t('Voir l’aperçu', 'View preview')} data-id="element-4393" data-mp="187uwy1savmzw" />
                  </button>
                </li>;
        })}
          </ul>}
      </div>

      <div className="border-t border-slate-100 px-4 py-3" data-id="element-4394" data-mp="1qwrt6b79cpkr">
        <p className="text-sm font-semibold text-slate-900" data-id="element-4395" data-mp="1qw9n6musmwzn">{t('Contexte', 'Context')}</p>
        <div className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-4396" data-mp="1d4lbulsdccjq">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-slate-200 text-xs text-slate-600" data-id="element-4397" data-mp="1h6q7tr6ahl9j">
            {categoryLabel}
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-slate-200 text-xs font-medium text-slate-600 uppercase" data-id="element-4398" data-mp="24ug7t1aqf0hd">
            {contentLang}
          </span>
        </div>
        {title.trim() && <p className="mt-2 text-xs text-slate-500 line-clamp-2" data-id="element-4399" data-mp="h04itfmr7v70">{title}</p>}
      </div>
    </motion.section>;
}